import { Box, Typography, Grid, Card, CardContent, Avatar, Chip, LinearProgress, Stack } from '@mui/material';
import { keyframes } from '@mui/system';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const slideIn = keyframes`
  from { opacity:0; transform:translateX(-20px); }
  to   { opacity:1; transform:translateX(0); }
`;
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

const players = [
  { name: 'Mamadou Diallo', pos: 'Attaquant', age: 19, club: 'AS Kaloum', country: 'Guinée', rating: 87, stats: [{ l: 'Vitesse', v: 92 }, { l: 'Technique', v: 88 }, { l: 'Physique', v: 80 }], badge: '🔥 En Vue', color: '#16a34a' },
  { name: 'Ibrahim Konaté', pos: 'Milieu Central', age: 21, club: 'Hafia FC', country: 'Guinée', rating: 85, stats: [{ l: 'Vision', v: 90 }, { l: 'Passes', v: 87 }, { l: 'Endurance', v: 84 }], badge: '⭐ Top Prospect', color: '#22c55e' },
  { name: 'Oumar Baldé', pos: 'Défenseur Central', age: 20, club: 'Horoya AC', country: 'Guinée', rating: 83, stats: [{ l: 'Défense', v: 89 }, { l: 'Aérien', v: 85 }, { l: 'Leadership', v: 82 }], badge: '🛡️ Solide', color: '#4ade80' },
];

const FeaturedProfiles = () => (
  <Box sx={{ py: 12, px: 3, background: 'linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)' }}>
    <Typography variant="overline" sx={{
      letterSpacing: 4, display: 'block', textAlign: 'center', mb: 1,
      background: 'linear-gradient(135deg, #16a34a, #22c55e)',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      fontWeight: 700,
    }}>
      Vedettes
    </Typography>
    <Typography variant="h2" sx={{ textAlign: 'center', color: '#0f172a', fontWeight: 800, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
      Profils en Vedette
    </Typography>
    <Typography variant="body1" sx={{ textAlign: 'center', color: '#94a3b8', mb: 8, maxWidth: 500, mx: 'auto' }}>
      Ces jeunes talents se démarquent cette saison par leurs performances exceptionnelles.
    </Typography>

    <Grid container spacing={4} maxWidth={1100} mx="auto">
      {players.map((p, i) => (
        <Grid item xs={12} md={4} key={i}>
          <Card elevation={0} sx={{
            bgcolor: '#ffffff',
            border: `1px solid ${p.color}33`,
            borderRadius: 4, overflow: 'visible', position: 'relative',
            animation: `${slideIn} .7s ease forwards`, animationDelay: `${i * .15}s`, opacity: 0,
            transition: 'all .35s',
            '&:hover': {
              transform: 'translateY(-8px)',
              border: `1px solid ${p.color}88`,
              boxShadow: `0 20px 60px ${p.color}28`,
            },
          }}>
            {/* Shimmering top border */}
            <Box sx={{
              height: 3, borderRadius: '4px 4px 0 0',
              background: `linear-gradient(90deg, transparent, ${p.color}, transparent)`,
              backgroundSize: '200% auto',
              animation: `${shimmer} 3s linear infinite`,
            }} />

            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <Avatar sx={{
                  width: 60, height: 60,
                  background: `linear-gradient(135deg, ${p.color}22, ${p.color}44)`,
                  border: `2px solid ${p.color}55`,
                  fontSize: '1.5rem', color: p.color, fontWeight: 700,
                }}>
                  {p.name.charAt(0)}
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, lineHeight: 1.2 }}>{p.name}</Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>{p.pos}</Typography>
                </Box>
                <Box sx={{
                  width: 48, height: 48, borderRadius: 2,
                  background: `linear-gradient(135deg, ${p.color}18, ${p.color}28)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                }}>
                  <Typography variant="h6" sx={{ color: p.color, fontWeight: 900, lineHeight: 1 }}>{p.rating}</Typography>
                  <StarIcon sx={{ fontSize: 12, color: p.color }} />
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} mb={3}>
                <Chip icon={<LocationOnIcon sx={{ fontSize: 14 }} />} label={p.country} size="small"
                  sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontSize: '.7rem' }} />
                <Chip label={`${p.age} ans`} size="small"
                  sx={{ bgcolor: `${p.color}15`, color: p.color, fontSize: '.7rem', fontWeight: 600 }} />
                <Chip label={p.badge} size="small"
                  sx={{ bgcolor: `${p.color}15`, color: p.color, fontSize: '.7rem', fontWeight: 600 }} />
              </Stack>

              {p.stats.map((s, j) => (
                <Box key={j} mb={1.5}>
                  <Stack direction="row" justifyContent="space-between" mb={.5}>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>{s.l}</Typography>
                    <Typography variant="caption" sx={{ color: p.color, fontWeight: 700 }}>{s.v}</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={s.v} sx={{
                    height: 4, borderRadius: 2, bgcolor: 'rgba(34,197,94,.1)',
                    '& .MuiLinearProgress-bar': {
                      background: `linear-gradient(90deg, ${p.color}, #22c55e)`,
                      borderRadius: 2,
                    },
                  }} />
                </Box>
              ))}

              <Typography variant="caption" sx={{ color: '#cbd5e1', mt: 1, display: 'block' }}>{p.club}</Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

export default FeaturedProfiles;